import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PiggyBank, Plus, Target, Trash2, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, EmptyState, Spinner } from "../components/common";

const ICONS = ["🏖️", "🚗", "🏠", "🎓", "💍", "✈️", "🎁", "📱", "🚑", "💰"];

function GoalCard({ goal, onChange }) {
  const [mode, setMode] = useState(null); // 'deposit' | 'withdraw'
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/savings/goals/${goal.goal_id}/${mode}`, { amount: val });
      toast.success(mode === "deposit" ? `Added to ${goal.name}` : `Moved back to wallet`);
      setMode(null); setAmount("");
      onChange(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${goal.name}"? Saved money returns to your wallet.`)) return;
    try {
      await api.delete(`/savings/goals/${goal.goal_id}`);
      toast.success("Goal deleted");
      onChange(null, goal.goal_id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`goal-card-${goal.goal_id}`} className="rounded-3xl bg-card border border-border p-5">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-2xl shrink-0">{goal.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold truncate">{goal.name}</h3>
            <button data-testid={`goal-delete-${goal.goal_id}`} onClick={del} className="text-muted-foreground hover:text-destructive shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            <span className="font-semibold text-foreground font-mono">{goal.saved_display}</span> of {goal.target_display}
          </p>
        </div>
      </div>

      <div className="mt-4 h-2.5 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${goal.progress}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
        <span>{goal.progress}% saved</span>
        <span>{goal.remaining_display} to go</span>
      </div>

      {mode ? (
        <div className="mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <input type="number" autoFocus placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} min="0" step="0.01"
              data-testid={`goal-amount-${goal.goal_id}`}
              className="w-full h-11 pl-7 pr-3 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm" />
          </div>
          <button data-testid={`goal-confirm-${goal.goal_id}`} onClick={act} disabled={busy}
            className="h-11 px-4 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60 flex items-center gap-1.5">
            {busy ? <Spinner className="w-4 h-4" /> : (mode === "deposit" ? "Add" : "Take out")}
          </button>
          <button onClick={() => { setMode(null); setAmount(""); }} className="h-11 w-11 grid place-items-center rounded-xl bg-secondary text-muted-foreground">
            <X size={18} />
          </button>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid={`goal-add-${goal.goal_id}`} onClick={() => setMode("deposit")}
            className="h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm transition-transform active:scale-95">Add money</button>
          <button data-testid={`goal-withdraw-${goal.goal_id}`} onClick={() => setMode("withdraw")}
            className="h-11 rounded-xl bg-secondary font-semibold text-sm transition-transform active:scale-95">Withdraw</button>
        </div>
      )}
    </div>
  );
}

export default function Savings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", target: "", icon: ICONS[0] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/savings/goals"); setData(r.data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const target = parseFloat(form.target);
    if (!form.name.trim()) { toast.error("Name your goal"); return; }
    if (!target || target <= 0) { toast.error("Set a target amount"); return; }
    setBusy(true);
    try {
      await api.post("/savings/goals", { name: form.name.trim(), target, icon: form.icon });
      toast.success("Goal created");
      setForm({ name: "", target: "", icon: ICONS[0] });
      setCreating(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const onGoalChange = (updated, deletedId) => {
    setData((d) => {
      let goals = d.goals;
      if (deletedId) goals = goals.filter((g) => g.goal_id !== deletedId);
      else if (updated) goals = goals.map((g) => g.goal_id === updated.goal_id ? updated : g);
      const total = goals.reduce((s, g) => s + g.saved, 0);
      return { ...d, goals, total_saved: total };
    });
    load();
  };

  if (loading || !data) return <FullLoader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Savings Goals" subtitle="Set money aside for what matters"
        action={<button data-testid="new-goal-btn" onClick={() => setCreating(!creating)}
          className="grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground transition-transform active:scale-90"><Plus size={20} /></button>} />

      <div className="rounded-3xl p-6 text-white shadow-[0_18px_44px_rgba(75,35,204,0.3)] mb-6"
        style={{ background: "linear-gradient(135deg,#6B3BF5,#4B23CC)" }}>
        <p className="text-white/70 text-xs tracking-[0.18em] uppercase font-semibold">Total saved</p>
        <div data-testid="total-saved" className="amount-figure text-4xl mt-1">{data.total_saved_display}</div>
      </div>

      {creating && (
        <form onSubmit={create} data-testid="new-goal-form" className="rounded-3xl bg-card border border-border p-5 mb-6 space-y-3">
          <input placeholder="Goal name (e.g. New phone)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="goal-name-input" className="w-full h-12 px-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm" />
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <input type="number" placeholder="Target amount" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} min="0" step="0.01"
              data-testid="goal-target-input" className="w-full h-12 pl-8 pr-4 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            {ICONS.map((ic) => (
              <button type="button" key={ic} onClick={() => setForm({ ...form, icon: ic })}
                className={`w-10 h-10 rounded-xl grid place-items-center text-xl transition-colors ${form.icon === ic ? "bg-primary/15 ring-2 ring-primary" : "bg-secondary"}`}>{ic}</button>
            ))}
          </div>
          <button type="submit" disabled={busy} data-testid="goal-create-submit"
            className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : "Create goal"}
          </button>
        </form>
      )}

      {data.goals.length === 0 ? (
        <EmptyState icon={Target} title="No savings goals yet"
          body="Create a goal and set money aside from your wallet to reach it.">
          <button data-testid="empty-new-goal" onClick={() => setCreating(true)}
            className="h-12 px-6 rounded-xl bg-primary text-primary-foreground font-semibold">Create your first goal</button>
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {data.goals.map((g) => <GoalCard key={g.goal_id} goal={g} onChange={onGoalChange} />)}
        </div>
      )}
    </div>
  );
}
